import { Link, useParams } from "react-router-dom";
import { articleMap, getArticlesByCategory, type Article } from "@/mocks/articles";
import { categoryMap } from "@/mocks/categories";
import { formatHeat } from "@/components/feature/ArticleCard";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";

export default function ArticlePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const article: Article | undefined = articleMap[slug];

  useSeo({
    title: article
      ? `${article.title}漫画解读 | ${article.title}人气与热度 - 漫天堂`
      : "未找到文章 - 漫天堂",
    description: article
      ? `${article.summary} 漫天堂带你了解${article.title}的人气热度、评分与追更指南。`
      : "很抱歉，你要找的文章不存在。",
    keywords: article
      ? `漫天堂,${article.title},${article.title}漫画,${article.title}评分,${article.title}热度,${article.title}推荐`
      : "漫天堂",
    canonicalPath: article ? `/article/${article.slug}` : "/",
    ogType: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://bestmanga.github.io/" },
              {
                "@type": "ListItem",
                position: 2,
                name: `${categoryMap[article.category]?.name ?? ""}漫画`,
                item: `https://bestmanga.github.io/category/${article.category}`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `https://bestmanga.github.io/article/${article.slug}`,
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary,
            image: [article.cover],
            author: { "@type": "Organization", name: "漫天堂编辑部" },
            publisher: { "@type": "Organization", name: "漫天堂" },
            datePublished: article.updatedAt,
            dateModified: article.updatedAt,
            articleSection: `${categoryMap[article.category]?.name ?? ""}漫画`,
            inLanguage: "zh-CN",
            mainEntityOfPage: `https://bestmanga.github.io/article/${article.slug}`,
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 text-center">
        <h1 className="text-2xl font-bold text-foreground-950">未找到该文章</h1>
        <p className="mt-3 text-sm text-foreground-600">很抱歉，你要找的内容不存在或已被移除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const cat = categoryMap[article.category];
  const related = getArticlesByCategory(article.category)
    .filter((a) => a.id !== article.id)
    .slice(0, 4);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb
        items={[
          { label: `${cat?.name ?? ""}漫画`, to: `/category/${article.category}` },
          { label: article.title },
        ]}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr]">
        <aside className="mx-auto w-full max-w-xs lg:max-w-none">
          <div className="sticky top-20 space-y-4">
            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
              <img
                src={article.cover}
                alt={`${article.title}漫画封面`}
                title={`${article.title} 高分漫画`}
                className="aspect-[3/4] w-full object-cover object-top"
              />
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-foreground-500">漫天堂评分</span>
                <span className="font-heading text-2xl font-extrabold text-primary-600">
                  {article.rating.toFixed(1)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                <span>热度</span>
                <span className="font-semibold text-foreground-950">{formatHeat(article.heat)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                <span>作者</span>
                <span className="font-semibold text-foreground-950">{article.author}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                <span>状态</span>
                <span className="font-semibold text-foreground-950">{article.status}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
                <span>已更新</span>
                <span className="font-semibold text-foreground-950">{article.chapters}话</span>
              </div>
            </div>
          </div>
        </aside>

        <article>
          <div className="flex items-center gap-2">
            <Link
              to={`/category/${article.category}`}
              className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
            >
              {cat?.name ?? ""}漫画
            </Link>
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-bold text-accent-900">
              {article.rating.toFixed(1)} 分
            </span>
          </div>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.updatedAt} 更新
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line"></i>
              已读 {article.readCount.toLocaleString()}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-fill text-primary-500"></i>
              {formatHeat(article.heat)} 热度
            </span>
          </div>

          <p className="mt-6 rounded-lg border-l-4 border-primary-500 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700 md:p-5">
            {article.summary}
          </p>

          <div className="mt-7 space-y-5">
            {article.content.map((para, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-800">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              查看同类榜单
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-background-100 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-200"
            >
              返回人气榜
            </Link>
          </div>
        </article>
      </div>

      {related.length > 0 && (
        <section className="mt-14">
          <h2 className="mb-6 text-xl font-bold text-foreground-950 md:text-2xl">
            同类 {cat?.name ?? ""}漫画推荐
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}